/**
 * Links de captação: post → clique → lead no CRM.
 *
 * Aqui se cadastra o destino (WhatsApp com mensagem pronta, ou um site), que
 * depois é escolhido no post. Cada conta que publica recebe o SEU link curto,
 * e o relatório mostra cliques e leads por rede e por post.
 *
 * O link da bio fica aqui também: no Instagram e no TikTok o link escrito na
 * legenda não é clicável — o da bio é o que a pessoa toca.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { PermissionGuard } from "@/components/PermissionGuard";
import DashboardLayout from "@/components/DashboardLayout";
import { usePermissions } from "@/hooks/usePermissions";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Archive, ArchiveRestore, ArrowLeft, Copy, FileText, Globe, Loader2, MessageCircle, Pencil, Plus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { NETWORK_LABELS, type SocialNetwork } from "@shared/social-publishing";
import {
  codigoDeRef,
  PAGINA_LIMITES,
  PAGINA_PADRAO,
  ROTULOS_DOS_CAMPOS,
  validarPagina,
  type CampoOpcional,
  type ModoDoCampo,
  type PaginaDeCaptacao,
} from "@shared/social-leads";
import { IconeRede } from "@/features/social/redes";

const REDES: SocialNetwork[] = ["instagram", "facebook", "tiktok", "youtube", "linkedin"];
type Periodo = 7 | 30 | 90;

type Form = {
  name: string;
  destination: "whatsapp" | "url" | "pagina";
  whatsappPhone: string;
  whatsappMessage: string;
  url: string;
  tagId: number | null;
  pagina: PaginaDeCaptacao;
};

const VAZIO: Form = {
  name: "",
  destination: "whatsapp",
  whatsappPhone: "",
  whatsappMessage: "Olá! Vi o post de vocês e quero saber mais.",
  url: "",
  tagId: null,
  pagina: PAGINA_PADRAO,
};

function taxa(leads: number, cliques: number): string {
  if (cliques === 0) return "—";
  return `${Math.round((leads / cliques) * 100)}%`;
}

async function copiar(texto: string) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success("Link copiado.");
  } catch {
    toast.message(texto);
  }
}

function CaptacaoContent() {
  const { can } = usePermissions();
  const podeVerNumeros = can("social:view_insights");
  const [periodo, setPeriodo] = useState<Periodo>(30);
  const utils = trpc.useUtils();
  const links = trpc.social.captacao.links.useQuery({ dias: periodo });
  const relatorio = trpc.social.captacao.relatorio.useQuery({ dias: periodo }, { enabled: podeVerNumeros });
  const [editando, setEditando] = useState<{ id: number | null; form: Form } | null>(null);
  const [bio, setBio] = useState<{ id: number; nome: string } | null>(null);

  const arquivar = trpc.social.captacao.arquivar.useMutation({
    onSuccess: () => void utils.social.captacao.links.invalidate(),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const totais = useMemo(() => {
    const porRede = new Map((relatorio.data?.porRede ?? []).map((r) => [r.network, r]));
    return REDES.map((rede) => ({ rede, cliques: porRede.get(rede)?.cliques ?? 0, leads: porRede.get(rede)?.leads ?? 0 }));
  }, [relatorio.data]);
  const soma = totais.reduce((a, t) => ({ cliques: a.cliques + t.cliques, leads: a.leads + t.leads }), { cliques: 0, leads: 0 });

  const novo = () =>
    setEditando({ id: null, form: { ...VAZIO, whatsappPhone: links.data?.whatsappDoWorkspace ?? "" } });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link href="/social" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Publicações
          </Link>
          <h1 className="mt-1 text-2xl font-semibold">Leads das redes</h1>
          <p className="text-sm text-muted-foreground">
            Links curtos que levam ao seu WhatsApp ou site e contam de qual rede e de qual post veio cada lead.
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={String(periodo)} onValueChange={(v) => setPeriodo(Number(v) as Periodo)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={novo} disabled={links.data?.enderecoPublicoPronto === false}>
            <Plus className="mr-2 h-4 w-4" /> Novo link
          </Button>
        </div>
      </div>

      {links.data?.enderecoPublicoPronto === false && (
        <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          O endereço público do LeadConversa não está configurado neste servidor — sem ele não dá para gerar link curto.
        </p>
      )}

      {/* ── Números por rede ─────────────────────────────────────────────── */}
      {podeVerNumeros && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Card>
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">Todas as redes</p>
              <p className="text-2xl font-semibold">{soma.leads}</p>
              <p className="text-xs text-muted-foreground">
                leads · {soma.cliques} cliques · {taxa(soma.leads, soma.cliques)}
              </p>
            </CardContent>
          </Card>
          {totais.map((t) => (
            <Card key={t.rede}>
              <CardContent className="pt-5">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <IconeRede rede={t.rede} className="h-3.5 w-3.5" /> {NETWORK_LABELS[t.rede]}
                </p>
                <p className="text-2xl font-semibold">{t.leads}</p>
                <p className="text-xs text-muted-foreground">
                  leads · {t.cliques} cliques · {taxa(t.leads, t.cliques)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {podeVerNumeros && (
        <p className="text-xs text-muted-foreground">
          Lead = contato que mandou a mensagem pronta (com o código do link) no WhatsApp, ou que preencheu a página de
          captação. Link para site conta só os cliques.
        </p>
      )}

      {/* ── Links ────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Links</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {links.isLoading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
          {links.data?.links.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhum link ainda. Crie um, escolha-o no post (o campo "Link de captação") e escreva {"{link}"} na legenda.
            </p>
          )}
          {links.data?.links.map((l) => (
            <div key={l.id} className={`flex flex-wrap items-center gap-3 rounded border p-3 ${l.isActive ? "" : "opacity-60"}`}>
              {l.destination === "whatsapp" ? (
                <MessageCircle className="h-5 w-5 text-emerald-600" />
              ) : l.destination === "pagina" ? (
                <FileText className="h-5 w-5 text-violet-600" />
              ) : (
                <Globe className="h-5 w-5 text-sky-600" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {l.name} {!l.isActive && <Badge variant="outline">Arquivado</Badge>}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {l.destination === "whatsapp"
                    ? `WhatsApp ${l.whatsappPhone} · "${l.whatsappMessage}"`
                    : l.destination === "pagina"
                      ? `Página de captação · "${l.pageConfig?.titulo ?? ""}"`
                      : l.url}
                </p>
              </div>
              {links.data?.veNumeros && (
              <div className="text-right text-sm">
                <p>
                  <strong>{l.leads}</strong> leads · <strong>{l.cliques}</strong> cliques
                </p>
                <div className="flex justify-end gap-1">
                  {l.porRede.map((r) => (
                    <span key={r.network} className="inline-flex items-center gap-0.5 text-xs text-muted-foreground" title={NETWORK_LABELS[r.network]}>
                      <IconeRede rede={r.network} className="h-3 w-3" colorido={false} /> {r.leads}/{r.cliques}
                    </span>
                  ))}
                </div>
              </div>
              )}
              <div className="flex gap-1">
                {l.isActive && (
                  <Button size="sm" variant="outline" onClick={() => setBio({ id: l.id, nome: l.name })}>
                    Link de bio
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Editar"
                  onClick={() =>
                    setEditando({
                      id: l.id,
                      form: {
                        name: l.name,
                        destination: l.destination as Form["destination"],
                        whatsappPhone: l.whatsappPhone ?? "",
                        whatsappMessage: l.whatsappMessage ?? "",
                        url: l.url ?? "",
                        tagId: l.tagId,
                        pagina: { ...PAGINA_PADRAO, ...(l.pageConfig ?? {}), campos: { ...PAGINA_PADRAO.campos, ...(l.pageConfig?.campos ?? {}) } },
                      },
                    })
                  }
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={l.isActive ? "Arquivar" : "Reativar"}
                  title={l.isActive ? "Arquivar (os links já publicados continuam funcionando)" : "Reativar"}
                  onClick={() => arquivar.mutate({ id: l.id, ativo: !l.isActive })}
                >
                  {l.isActive ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Posts e leads ────────────────────────────────────────────────── */}
      {podeVerNumeros && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Posts que mais trouxeram leads</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(relatorio.data?.porPost.length ?? 0) === 0 && (
                <p className="text-sm text-muted-foreground">Nenhum clique no período.</p>
              )}
              {relatorio.data?.porPost.map((p) => (
                <div key={`${p.postId}-${p.network}`} className="flex items-center gap-2 text-sm">
                  <IconeRede rede={p.network} />
                  <span className="min-w-0 flex-1 truncate" title={p.caption}>
                    {p.caption?.replaceAll("{link}", "").replace(/\s+/g, " ").trim().slice(0, 80) || "(sem legenda)"}
                  </span>
                  <span className="whitespace-nowrap text-muted-foreground">
                    <strong className="text-foreground">{p.leads}</strong> leads · {p.cliques} cliques
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Últimos leads</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(relatorio.data?.leads.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">Nenhum lead no período.</p>}
              {relatorio.data?.leads.map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-sm">
                  <IconeRede rede={l.network} />
                  {relatorio.data?.veContatos ? (
                    <Link href={`/contacts/${l.contactId}`} className="min-w-0 flex-1 truncate font-medium text-primary hover:underline">
                      {l.contato}
                    </Link>
                  ) : (
                    <span className="min-w-0 flex-1 truncate">{l.contato}</span>
                  )}
                  <span className="truncate text-xs text-muted-foreground">{l.nomeDoLink}</span>
                  <span className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(l.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {editando && (
        <EditarLink
          inicial={editando}
          etiquetas={links.data?.etiquetas ?? []}
          onFechar={() => setEditando(null)}
        />
      )}
      {bio && <LinkDeBio id={bio.id} nome={bio.nome} onFechar={() => setBio(null)} />}
    </div>
  );
}

function EditarLink({
  inicial,
  etiquetas,
  onFechar,
}: {
  inicial: { id: number | null; form: Form };
  etiquetas: Array<{ id: number; name: string }>;
  onFechar: () => void;
}) {
  const utils = trpc.useUtils();
  const [f, setF] = useState<Form>(inicial.form);
  const set = (patch: Partial<Form>) => setF((atual) => ({ ...atual, ...patch }));
  const ok = () => {
    void utils.social.captacao.links.invalidate();
    toast.success("Link salvo.");
    onFechar();
  };
  const criar = trpc.social.captacao.criar.useMutation({ onSuccess: ok, onError: (e) => toast.error(toUserFriendlyError(e)) });
  const atualizar = trpc.social.captacao.atualizar.useMutation({ onSuccess: ok, onError: (e) => toast.error(toUserFriendlyError(e)) });
  const salvando = criar.isPending || atualizar.isPending;
  const dados = {
    name: f.name,
    destination: f.destination,
    whatsappPhone: f.whatsappPhone || null,
    whatsappMessage: f.whatsappMessage || null,
    url: f.url || null,
    tagId: f.tagId,
    pageConfig: f.destination === "pagina" ? f.pagina : null,
  };
  const errosDaPagina = f.destination === "pagina" ? validarPagina(f.pagina) : [];

  return (
    <Dialog open onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className={`max-h-[92vh] overflow-y-auto ${f.destination === "pagina" ? "sm:max-w-5xl" : "sm:max-w-lg"}`}>
        <DialogHeader>
          <DialogTitle>{inicial.id ? "Editar link" : "Novo link de captação"}</DialogTitle>
          <DialogDescription>Depois, escolha este link no post e escreva {"{link}"} na legenda.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="nome-link">Nome (para você reconhecer)</Label>
            <Input id="nome-link" value={f.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} placeholder="Ex.: Promoção de outubro" />
          </div>
          <div className="space-y-1">
            <Label>Para onde leva</Label>
            <RadioGroup value={f.destination} onValueChange={(v) => set({ destination: v as Form["destination"] })} className="flex flex-wrap gap-x-4 gap-y-2">
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="whatsapp" /> WhatsApp (gera lead no CRM)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="pagina" /> Página de captação (formulário, gera lead)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="url" /> Site (conta cliques)
              </label>
            </RadioGroup>
          </div>
          {f.destination === "pagina" ? (
            <EditorDaPagina
              pagina={f.pagina}
              onChange={(pagina) => set({ pagina })}
              whatsapp={f.whatsappPhone}
              onWhatsApp={(whatsappPhone) => set({ whatsappPhone })}
              etiqueta={
                <SeletorDeEtiqueta etiquetas={etiquetas} valor={f.tagId} onChange={(tagId) => set({ tagId })} />
              }
            />
          ) : f.destination === "whatsapp" ? (
            <>
              <div className="space-y-1">
                <Label htmlFor="fone">Número do WhatsApp</Label>
                <Input id="fone" value={f.whatsappPhone} onChange={(e) => set({ whatsappPhone: e.target.value })} placeholder="(11) 98765-4321" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="msg">Mensagem que a pessoa vai mandar</Label>
                <Textarea id="msg" rows={3} maxLength={500} value={f.whatsappMessage} onChange={(e) => set({ whatsappMessage: e.target.value })} />
                <p className="text-xs text-muted-foreground">
                  Vai assim: "{f.whatsappMessage.trim()} (ref. {codigoDeRef("k7m2p9q")})" — o código no fim é o que diz de qual
                  rede e post a pessoa veio. Se ela apagar o código antes de enviar, o lead chega sem origem.
                </p>
              </div>
              <SeletorDeEtiqueta etiquetas={etiquetas} valor={f.tagId} onChange={(tagId) => set({ tagId })} />
            </>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="url">Endereço do site</Label>
              <Input id="url" value={f.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://" />
              <p className="text-xs text-muted-foreground">
                O LeadConversa acrescenta utm_source (a rede), utm_medium=social e utm_campaign, para o seu Google Analytics separar as redes.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            disabled={salvando || !f.name.trim() || errosDaPagina.length > 0}
            title={errosDaPagina.join(" ") || undefined}
            onClick={() => (inicial.id ? atualizar.mutate({ id: inicial.id, data: dados }) : criar.mutate(dados))}
          >
            {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SeletorDeEtiqueta({
  etiquetas,
  valor,
  onChange,
}: {
  etiquetas: Array<{ id: number; name: string }>;
  valor: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Label>Etiqueta no contato (opcional)</Label>
      <Select value={valor == null ? "nenhuma" : String(valor)} onValueChange={(v) => onChange(v === "nenhuma" ? null : Number(v))}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="nenhuma">Sem etiqueta</SelectItem>
          {etiquetas.map((t) => (
            <SelectItem key={t.id} value={String(t.id)}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        A etiqueta dispara os fluxos de "etiqueta adicionada" — dá para responder o lead na hora ou criar a negociação.
      </p>
    </div>
  );
}

const MODOS: Record<ModoDoCampo, string> = { nao: "Não pedir", opcional: "Opcional", obrigatorio: "Obrigatório" };

/** Editor da página de captação, com a pré-visualização ao lado. */
function EditorDaPagina({
  pagina,
  onChange,
  whatsapp,
  onWhatsApp,
  etiqueta,
}: {
  pagina: PaginaDeCaptacao;
  onChange: (p: PaginaDeCaptacao) => void;
  whatsapp: string;
  onWhatsApp: (v: string) => void;
  etiqueta: React.ReactNode;
}) {
  const set = (patch: Partial<PaginaDeCaptacao>) => onChange({ ...pagina, ...patch });
  // Pré-visualização só depois de a pessoa parar de digitar.
  const [atrasada, setAtrasada] = useState(pagina);
  useEffect(() => {
    const t = setTimeout(() => setAtrasada(pagina), 400);
    return () => clearTimeout(t);
  }, [pagina]);
  const previa = trpc.social.captacao.previaDaPagina.useQuery(
    { ...atrasada, politicaUrl: atrasada.politicaUrl || null },
    { enabled: validarPagina(atrasada).length === 0, placeholderData: (anterior) => anterior, meta: { erroNaTela: true } },
  );
  const erros = validarPagina(pagina);

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="pg-titulo">Título</Label>
          <Input id="pg-titulo" value={pagina.titulo} maxLength={PAGINA_LIMITES.titulo} onChange={(e) => set({ titulo: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pg-desc">Texto da página</Label>
          <Textarea id="pg-desc" rows={3} value={pagina.descricao} maxLength={PAGINA_LIMITES.descricao} onChange={(e) => set({ descricao: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label>Campos</Label>
          <p className="text-xs text-muted-foreground">Nome e WhatsApp são sempre pedidos.</p>
          {(Object.keys(ROTULOS_DOS_CAMPOS) as CampoOpcional[]).map((c) => (
            <div key={c} className="flex items-center justify-between gap-3 text-sm">
              <span>{ROTULOS_DOS_CAMPOS[c]}</span>
              <Select value={pagina.campos[c]} onValueChange={(v) => set({ campos: { ...pagina.campos, [c]: v as ModoDoCampo } })}>
                <SelectTrigger className="h-8 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(MODOS) as ModoDoCampo[]).map((m) => (
                    <SelectItem key={m} value={m}>
                      {MODOS[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="pg-botao">Texto do botão</Label>
            <Input id="pg-botao" value={pagina.botao} maxLength={PAGINA_LIMITES.botao} onChange={(e) => set({ botao: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pg-cor">Cor</Label>
            <div className="flex gap-2">
              <input
                id="pg-cor"
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(pagina.cor) ? pagina.cor : PAGINA_PADRAO.cor}
                onChange={(e) => set({ cor: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border"
              />
              <Input value={pagina.cor} maxLength={7} onChange={(e) => set({ cor: e.target.value })} />
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="pg-obrigado">Mensagem depois de enviar</Label>
          <Textarea id="pg-obrigado" rows={2} value={pagina.agradecimento} maxLength={PAGINA_LIMITES.agradecimento} onChange={(e) => set({ agradecimento: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pg-politica">Política de privacidade (opcional)</Label>
          <Input id="pg-politica" value={pagina.politicaUrl ?? ""} placeholder="https://" onChange={(e) => set({ politicaUrl: e.target.value || null })} />
        </div>
        <div className="space-y-2 rounded border p-3">
          <label className="flex items-center justify-between gap-4 text-sm">
            Oferecer "Falar no WhatsApp agora" depois de enviar
            <Switch checked={pagina.oferecerWhatsApp} onCheckedChange={(v) => set({ oferecerWhatsApp: v })} />
          </label>
          {pagina.oferecerWhatsApp && (
            <Input value={whatsapp} onChange={(e) => onWhatsApp(e.target.value)} placeholder="Número do WhatsApp: (11) 98765-4321" />
          )}
        </div>
        {etiqueta}
        {erros.length > 0 && (
          <ul className="list-disc space-y-1 rounded border border-amber-300 bg-amber-50 py-2 pl-6 pr-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            {erros.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">
          Quem preenche entra no CRM com a rede e o post de origem, e o aceite de contato fica registrado com data e hora.
        </p>
      </div>
      <div className="space-y-1">
        <Label>Como a página aparece</Label>
        <div className="overflow-hidden rounded-lg border bg-muted/40">
          {previa.data ? (
            <iframe title="Pré-visualização da página de captação" srcDoc={previa.data.html} sandbox="" className="h-[620px] w-full" />
          ) : (
            <div className="flex h-[620px] items-center justify-center text-sm text-muted-foreground">
              {previa.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Corrija os avisos para ver a página."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LinkDeBio({ id, nome, onFechar }: { id: number; nome: string; onFechar: () => void }) {
  const [urls, setUrls] = useState<Partial<Record<SocialNetwork, string>>>({});
  const gerar = trpc.social.captacao.linkDeBio.useMutation({ onError: (e) => toast.error(toUserFriendlyError(e)) });
  return (
    <Dialog open onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link de bio — {nome}</DialogTitle>
          <DialogDescription>
            Um link para cada rede, para os cliques e leads da bio aparecerem separados. Cole na bio do perfil.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {REDES.map((rede) => (
            <div key={rede} className="flex items-center gap-2">
              <IconeRede rede={rede} />
              <span className="w-24 text-sm">{NETWORK_LABELS[rede]}</span>
              {urls[rede] ? (
                <>
                  <Input readOnly value={urls[rede]} className="h-8 flex-1 text-xs" />
                  <Button size="icon" variant="ghost" aria-label="Copiar" onClick={() => void copiar(urls[rede]!)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={gerar.isPending}
                  onClick={async () => {
                    const r = await gerar.mutateAsync({ id, network: rede }).catch(() => null);
                    if (r) setUrls((u) => ({ ...u, [rede]: r.url }));
                  }}
                >
                  Gerar link
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SocialCaptacao() {
  return (
    <PermissionGuard permission="social:publish">
      <DashboardLayout>
        <CaptacaoContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
